/**
 * Assumption Manifest Builder
 *
 * Builds a manifest of all assumptions used in generating a report.
 * This is your legal and psychological shield when charging money.
 *
 * Store this:
 * - Inside the story payload
 * - Inside the PDF metadata
 * - Optionally visible in UI
 */

import type {
  AssumptionManifest,
  ManifestEntry,
  AssumptionSetName,
  ResolvedAssumption,
} from './types';
import { resolveAssumptions, getAvailableAssumptionSets, type ResolveOptions } from './resolveAssumptions';

// ==================== ENGINE VERSION ====================

/**
 * Current engine version.
 * Bump this when calculation logic changes (not just assumption updates).
 */
export const ENGINE_VERSION = 'v0.9.3';

// ==================== MANIFEST BUILDER ====================

/**
 * Tracks which assumptions have been resolved during a calculation.
 * Call `track()` after each resolveAssumptions call, then `build()` at the end.
 */
export class ManifestBuilder {
  private entries: ManifestEntry[] = [];
  private engineVersion: string;

  constructor(engineVersion: string = ENGINE_VERSION) {
    this.engineVersion = engineVersion;
  }

  /**
   * Track a resolved assumption for the manifest
   */
  track<T>(resolved: ResolvedAssumption<T>): void {
    // Avoid duplicates
    const exists = this.entries.some(
      (e) => e.key === resolved.meta.assumption_set && e.version === resolved.meta.version
    );
    if (!exists) {
      this.entries.push({
        key: resolved.meta.assumption_set,
        version: resolved.meta.version,
        source: resolved.meta.source,
        last_checked: resolved.meta.last_checked,
      });
    }
  }

  /**
   * Build the final manifest
   */
  build(): AssumptionManifest {
    return {
      generated_at: new Date().toISOString(),
      assumption_manifest: [...this.entries],
      engine_version: this.engineVersion,
    };
  }

  /**
   * Reset the builder for a new calculation
   */
  reset(): void {
    this.entries = [];
  }
}

// ==================== CONVENIENCE FUNCTIONS ====================

/**
 * Build a complete manifest for all assumption sets at a given date.
 * Use this when you want to document ALL assumptions, not just the ones
 * explicitly used in a particular calculation.
 *
 * @param options - Resolution options (date)
 * @returns Complete manifest with all assumption sets
 */
export function buildFullManifest(options: ResolveOptions = {}): AssumptionManifest {
  const builder = new ManifestBuilder();
  const setNames = getAvailableAssumptionSets();

  for (const setName of setNames) {
    const resolved = resolveAssumptions(setName, options);
    builder.track(resolved);
  }

  return builder.build();
}

/**
 * Build a manifest for specific assumption sets.
 * Use this when you know exactly which assumptions were used.
 *
 * @param setNames - The assumption sets that were used
 * @param options - Resolution options (date)
 * @returns Manifest with specified assumption sets
 */
export function buildManifestFor(
  setNames: AssumptionSetName[],
  options: ResolveOptions = {}
): AssumptionManifest {
  const builder = new ManifestBuilder();

  for (const setName of setNames) {
    const resolved = resolveAssumptions(setName, options);
    builder.track(resolved);
  }

  return builder.build();
}

// ==================== DISPLAY HELPERS ====================

/**
 * Format manifest for UI footer (concise)
 */
export function formatManifestForUI(manifest: AssumptionManifest): string {
  const dates = manifest.assumption_manifest.map((e) => new Date(e.last_checked));
  const latestCheck = new Date(Math.max(...dates.map((d) => d.getTime())));
  const monthYear = latestCheck.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

  return `Based on government statistics and industry reference data. Last checked: ${monthYear}. Values are typical reference figures.`;
}

/**
 * Format manifest for PDF footer (minimal)
 */
export function formatManifestForPDF(manifest: AssumptionManifest): string {
  const dates = manifest.assumption_manifest.map((e) => new Date(e.last_checked));
  const latestCheck = new Date(Math.max(...dates.map((d) => d.getTime())));
  const monthYear = latestCheck.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

  const sourceNames = [...new Set(manifest.assumption_manifest.map((e) => {
    // Extract short name from source
    if (e.source.includes('Ofgem')) return 'Ofgem';
    if (e.source.includes('DESNZ')) return 'DESNZ';
    if (e.source.includes('SEDBUK')) return 'SEDBUK';
    if (e.source.includes('HHIC') || e.source.includes('MCS')) return 'Industry data';
    return 'Reference data';
  }))].slice(0, 3);

  return `Assumptions current as of ${monthYear}. Sources include ${sourceNames.join(', ')}. Values are typical reference figures and may change over time.`;
}

/**
 * Get expandable source list for UI
 */
export function getSourceList(manifest: AssumptionManifest): Array<{
  category: string;
  source: string;
  lastChecked: string;
}> {
  return manifest.assumption_manifest.map((entry) => ({
    category: formatCategoryName(entry.key),
    source: entry.source,
    lastChecked: new Date(entry.last_checked).toLocaleDateString('en-GB', {
      month: 'short',
      year: 'numeric',
    }),
  }));
}

function formatCategoryName(key: string): string {
  const names: Record<string, string> = {
    energy_prices: 'Energy prices',
    carbon_factors: 'Carbon factors',
    system_lifespans: 'System lifespans',
    efficiency_bands: 'Efficiency ratings',
    demand_models: 'Heat demand',
    behavioural_defaults: 'Usage patterns',
  };
  return names[key] || key;
}
